import './styles.css';
import { Auth0Provider } from '@auth0/auth0-react';
import { isStaleChunkError, rememberReturnTo } from '@iqd/shared-app';
import * as Sentry from '@sentry/react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';

import App from './app/app';
import { AskDerek } from './app/features/ask-derek';
import { ApolloProvider } from './app/providers/apollo-provider';
import { Auth0Bridge } from './app/providers/auth0-bridge';
import { ThemeProvider } from './app/providers/theme-provider';
import { isAbortError } from './app/shared/utils/abort-error';
import { normaliseHostPath } from './app/shared/utils/host-path';
import { environment } from './environments/environment';

/**
 * Old links, in either shape they were written in.
 *
 * Angular put route parameters in the path — `/quoting/quotes;quoteId=12` —
 * and React Router reads that as one segment matching no route. The React
 * screens that replaced it moved those, and the open tab or report of a dozen
 * other screens, into the query string; both are path segments now. The
 * remote used to convert the Angular shape as the host handed each path down;
 * with no host, all of it has to happen here, before the router reads the URL.
 * People have these bookmarked and other systems link to them.
 */
function normaliseUrl() {
  const current = `${window.location.pathname}${window.location.search}`;
  const normalised = normaliseHostPath(current);
  if (normalised !== current) {
    window.history.replaceState(null, '', `${normalised}${window.location.hash}`);
  }
}

normaliseUrl();

if (environment.sentry.enabled) {
  Sentry.init({
    dsn: environment.sentry.dsn,
    release: environment.version,
    environment: environment.sentry.environment,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.2,
    // A tab open across a deploy asks for a chunk staging has already deleted,
    // and every lazy route then fails the same way. `NewBuildNotice` puts a
    // dialog up saying to reload, which is the whole of the answer, so
    // reporting it as well only files one issue per stale tab per deploy about
    // a build that is working. Dropped here rather than at the one place that
    // captures it, because the same failure also arrives unhandled from
    // dynamic imports outside a route.
    //
    // Cancelled requests go too: a page left mid-refetch rejects with one, and
    // Sentry files it, stackless, under the page navigated to.
    beforeSend(event, hint) {
      if (isAbortError(hint.originalException)) return null;
      const thrown = hint.originalException ?? event.exception?.values?.[0]?.value;
      return isStaleChunkError(thrown) ? null : event;
    },
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
  });

  // The replay recorder is about 120KB, and it was in the chunk every page
  // waits on before it can draw anything. Nothing here records a session —
  // `replaysSessionSampleRate` is 0 — so all it ever does is keep a buffer to
  // attach to an error, and that buffer is no use until there is an error.
  // Added once the browser is idle instead, out of its own chunk.
  //
  // The window between `init` and this landing has no buffer to attach, so an
  // error thrown during startup arrives without a replay. That is the price of
  // the 120KB; the timeout keeps the window to a few seconds at worst.
  const addReplay = () => {
    import('./sentry-replay')
      .then(({ replayIntegration }) => Sentry.addIntegration(replayIntegration()))
      // Replay is a nicety. Losing it must not take the error reporting with it.
      .catch(() => undefined);
  };

  if ('requestIdleCallback' in window) {
    requestIdleCallback(addReplay, { timeout: 5000 });
  } else {
    setTimeout(addReplay, 2000);
  }
}

// A data router, not <BrowserRouter>: `useUnsavedChangesGuard` holds a
// navigation away from the estimator with `useBlocker`, and that needs the
// data-router context. The whole app hangs off one splat route — the routes
// themselves stay declarative inside <App />.
const router = createBrowserRouter([
  {
    path: '*',
    element: (
      <Auth0Bridge>
        <ApolloProvider>
          <App />
          {/* Not a route: the assistant is drawn on every page. */}
          <AskDerek />
        </ApolloProvider>
      </Auth0Bridge>
    ),
  },
]);

createRoot(document.getElementById('root') as HTMLElement).render(
  <StrictMode>
    {/* Outside the router: the theme is chrome, not a route, and the attribute
        it writes is on the document rather than on anything React renders. */}
    <ThemeProvider>
      <Auth0Provider
        domain={environment.auth0.domain}
        clientId={environment.auth0.clientId}
        // Auth0 recommends renewing through a first-party silent-auth iframe over
        // holding a refresh token, when the Auth0 domain is same-site with the
        // app: nothing renewable is left at rest for an XSS to read. Where that
        // does not hold — localhost — fall back to refresh tokens.
        useRefreshTokens={!environment.auth0.useSilentAuth}
        useRefreshTokensFallback={false}
        cacheLocation={environment.auth0.useSilentAuth ? 'memory' : 'localstorage'}
        // Where a sign-in came from. Callers put it in `appState.returnTo`,
        // and without this nothing reads it — every sign-in landed on the
        // dashboard, including one started because the session expired
        // mid-screen, which then quietly moved you somewhere else.
        //
        // Remembered as well as navigated to: this fires before the SDK
        // reports itself loaded, so `AuthCallbackPage` can still render after
        // this navigation has moved the URL, and has to send people to the
        // same place rather than to the dashboard.
        onRedirectCallback={(appState) => {
          const target = appState?.returnTo ?? '/';
          rememberReturnTo(target);
          void router.navigate(target, { replace: true });
        }}
        authorizationParams={{
          audience: environment.auth0.audience,
          redirect_uri: `${window.location.origin}/callback`,
          scope: 'openid profile email',
        }}
      >
        <RouterProvider router={router} />
      </Auth0Provider>
    </ThemeProvider>
  </StrictMode>,
);
